import React, { FC, useEffect, useRef, useState } from "react";
import { Badge, Box, Button, Flex, HStack, Input, Text, VStack } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";

import { EvalCategoryModel } from "@shared/models";
import { actions, selectors } from "@containers/Evaluation/store";
import { EVAL_CSV_MAX_FILE_SIZE } from "@containers/Evaluation/constants";
import { parseEvalCsv } from "@containers/Evaluation/utils";
import { FONT_WEIGHT } from "@assets/theme/foundations/textStyle";

interface RunEvalOverlayProps {
  onClose: () => void;
}

const EvalCategoryItem: FC<{ category: EvalCategoryModel }> = ({ category }) => (
  <Box borderWidth="1px" borderColor="gray.50" borderRadius="8px" px="12px" py="8px">
    <Flex justify="space-between" align="center" mb="4px">
      <Text fontSize="14px" fontWeight={FONT_WEIGHT.HEAVY}>
        {category.name}
      </Text>
      {!!category.requiredFields.length && (
        <HStack gap="4px">
          {category.requiredFields.map((field) => (
            <Badge key={field} colorPalette="orange" fontSize="10px">
              {field}
            </Badge>
          ))}
        </HStack>
      )}
    </Flex>
    <Text fontSize="12px" color="brand.grey">
      {category.description}
    </Text>
  </Box>
);

const RunEvalOverlay: FC<RunEvalOverlayProps> = ({ onClose }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const categories = useSelector(selectors.getEvalCategories());
  const isRunning = useSelector(selectors.getIsEvalRunning());

  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const [caseCount, setCaseCount] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!categories) {
      dispatch(actions.getEvalCategories.request());
    }
  }, [categories, dispatch]);

  const handleChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setError("");
    setCsvContent(null);

    if (file.size > EVAL_CSV_MAX_FILE_SIZE) {
      setError(`File is too large. Maximum size is ${EVAL_CSV_MAX_FILE_SIZE / (1024 * 1024)}MB.`);
      return;
    }

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result;

      if (typeof text !== "string") return;

      const { caseCount: parsedCaseCount, error: parseError } = parseEvalCsv(text);

      if (parseError) {
        setError(parseError);
        return;
      }

      setCsvContent(text);
      setCaseCount(parsedCaseCount);
    };
    reader.readAsText(file);
  };

  const handleDownloadTemplate = () => dispatch(actions.downloadEvalTemplate.request());

  const handleRunEvaluation = () => {
    if (!csvContent) return;

    dispatch(actions.runEvalFromCsv.request({ csv: csvContent, cb: onClose }));
  };

  return (
    <VStack gap="24px" align="stretch" pb="24px">
      <VStack gap="8px" align="stretch">
        <Text fontSize="14px" fontWeight={FONT_WEIGHT.HEAVY}>
          Available categories
        </Text>
        {(categories || []).map((category) => (
          <EvalCategoryItem key={category.name} category={category} />
        ))}
      </VStack>

      <VStack gap="8px" align="stretch">
        <Text fontSize="14px" fontWeight={FONT_WEIGHT.HEAVY}>
          1. Download the CSV template
        </Text>
        <Button variant="secondary" w="fit-content" onClick={handleDownloadTemplate}>
          Download Template
        </Button>
      </VStack>

      <VStack gap="8px" align="stretch">
        <Text fontSize="14px" fontWeight={FONT_WEIGHT.HEAVY}>
          2. Fill it with your test cases and upload
        </Text>
        <Input ref={fileInputRef} type="file" accept=".csv" onChange={handleChangeFile} display="none" />
        <Button variant="secondary" w="fit-content" onClick={() => fileInputRef.current?.click()}>
          {fileName || "Choose CSV File"}
        </Button>
        {!!error && (
          <Text fontSize="12px" color="brand.error">
            {error}
          </Text>
        )}
        {!!csvContent && (
          <Text fontSize="12px" color="brand.grey">
            {fileName} — {caseCount} test case{caseCount === 1 ? "" : "s"} found
          </Text>
        )}
      </VStack>

      <Box>
        <Button variant="primary" w="176px" h="48px" disabled={!csvContent || isRunning} onClick={handleRunEvaluation}>
          {isRunning ? "Starting..." : "Run Evaluation"}
        </Button>
        {isRunning && (
          <Text fontSize="12px" color="brand.grey" mt="8px">
            Every case is answered and scored before the run is saved, so this can take a few minutes.
          </Text>
        )}
      </Box>
    </VStack>
  );
};

export default RunEvalOverlay;
